import { LitElement, css, html, nothing, unsafeHTML } from '@lit'

class NovaGiftMessage extends LitElement {
  static properties = {
    note: {
      type: String,
    },
    type: {
      type: String,
    },
    title: {
      type: String,
    },
    description: {
      type: String,
    },
    characterCount: {
      type: Number,
      state: true,
    }
  }

  $textarea = null
  debounceTimeout = null
  maxLength = 160

  constructor() {
    super()
    this.characterCount = 0
  }

  firstUpdated(changedProps) {
    super.firstUpdated?.(changedProps)
    this.$textarea = this.shadowRoot.querySelector('textarea')
    if (this.$textarea) {
      this.$textarea.addEventListener('input', this._handleInput)
      this.$textarea.addEventListener('keydown', this._handleKeyDown)
    }
  }

  disconnectedCallback() {
    if (this.$textarea) {
      this.$textarea.removeEventListener('input', this._handleInput)
      this.$textarea.removeEventListener('keydown', this._handleKeyDown)
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
    super.disconnectedCallback()
  }

  _handleKeyDown = (event) => {
    const textarea = event.target

    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
      'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'
    ]

    if (textarea.value.length >= this.maxLength && !allowedKeys.includes(event.key)) {
      event.preventDefault()
    }
  }

  _handleInput = (event) => {
    const message = event.target.value

    const limitedMessage = message.slice(0, this.maxLength)
    if (message.length > this.maxLength) {
      event.target.value = limitedMessage
    }

    this.characterCount = limitedMessage.length

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    this.debounceTimeout = setTimeout(() => {
      this.updateCartNote(limitedMessage)
    }, 500)
  }

  async updateCartNote(note) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note }),
      })

      if (!response.ok) {
        throw new Error('Error updating cart note')
      }
    } catch (error) {
      console.error('Error updating cart note:', error.message)
    }
  }

  render() {
    return html`
      <div class="gift-message gift-message--${this.type}">
        ${this.type == 'accordion' ? html`
            <sl-details summary="${this.title}" .open=${this.note?.trim() ? true : false}>
              <div slot="expand-icon">
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='8'
                  height='9'
                  viewBox='0 0 8 9'
                  fill='none'
                  aria-hidden='true'
                  focusable='false'
                  role='presentation'
                  class='icon icon-plus'
                >
                  <path d="M4.625 4.125H8V5.125H4.625V8.5H3.625V5.125H0V4.125H3.625V0.5H4.625V4.125Z" fill="currentColor"/>
                </svg>
              </div>
              <div slot="collapse-icon">
                <svg
                  width="8"
                  height="1"
                  viewBox="0 0 8 1"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8 1H0V0H8V1Z" fill="currentColor"/>
                </svg>
              </div>
              <div class="gift-message__wrapper">
                <div class="gift-message__description">
                  ${this.description}
                </div>
                <div class="gift-message__content">
                  <textarea
                    rows="4"
                    maxlength="${this.maxLength}"
                  >${this.note}</textarea>
                  <div class="gift-message__counter">
                    <span class="gift-message__counter-text">
                      ${this.characterCount}/${this.maxLength}
                    </span>
                  </div>
                </div>
              </div>
            </sl-details>
          ` : html`
            <div class="gift-message__head">
              <div class="gift-message__header">
                ${this.title}
              </div>

              <div class="gift-message__description">
                ${this.description}
              </div>
            </div>
            <div class="gift-message__wrapper">
              <div class="gift-message__content">
                <textarea
                  rows="4"
                  maxlength="${this.maxLength}"
                >${this.note}</textarea>
                <div class="gift-message__counter">
                  <span class="gift-message__counter-text">
                    ${this.characterCount}/${this.maxLength}
                  </span>
                </div>
              </div>
            </div>
          `
        }
      </div>
    `
  }

  static styles = css`
    :host {
      border-top: 1px solid var(--nova-brand-grey-50);
      border-bottom: 1px solid var(--nova-brand-grey-50);
    }

    .gift-message {
      padding: 16px 8px;
    }

    sl-details::part(base) {
      border: 0;
    }

    sl-details::part(header) {
      padding: 16px 8px;
    }

    sl-details[open]::part(header) {
      padding-bottom: 4px;
    }

    sl-details[open]::part(summary-icon) {
      rotate: 0deg;
      margin-top: -6px;
    }

    sl-details::part(content) {
      padding: 0 8px 10px;
    }

    .gift-message__description {
      margin-top: 4px;
    }

    .gift-message__content {
      margin-top: 16px;
      margin-left: -8px;
      margin-right: -8px;
    }

    .gift-message__content textarea {
      width: 100%;
      padding: 10px;
      box-sizing: border-box;
      min-height: 100px;
      border: 1px solid var(--nova-brand-grey-50);
      resize: vertical;
      display: block;
    }

    .gift-message__content textarea:focus {
      outline: none;
      border-color: var(--nova-brand-ink);
    }

    .gift-message__counter {
      padding: 4px 8px 0;
      display: flex  ;
      justify-content: flex-end;
    }

    .gift-message__header {
      font-size: 20px;
    }

    .gift-message--accordion {
      padding: 0;
    }

    .gift-message--accordion .gift-message__description {
      margin-top: 0;
    }

    @media screen and (min-width: 750px) {
      .gift-message--cart {
        border-bottom: 0;
        padding: 32px 0 10px 16px;
        display: flex;
        gap: 32px;
      }

      .gift-message--cart .gift-message__head {
        min-width: 190px;
      }

      .gift-message--cart .gift-message__wrapper {
        width: 100%;
      }

      .gift-message--cart .gift-message__description {
        margin-top: 8px;
      }

      .gift-message--cart .gift-message__content {
        margin: 0;
      }
    }
  `
}

window.customElements.define('nova-gift-message', NovaGiftMessage)
